import React, { useState, useContext } from 'react'
import { Modal, Button, Form } from 'react-bootstrap'
import Select from 'react-select'
import _ from 'lodash'
import { FiAlertTriangle } from 'react-icons/fi'

import AppConfigContext from '../../../app_layout/app_config_context'
import { ApiHelper } from '../../../../helpers/apihelper'
import showToastNotification from '../../../widgets/toastify'
import * as Constants from '../../../../helpers/constants'
import { CANCEL_REASONS } from '../../helpers/constants'

const initialState = {
  showCancelSubscriptionModal: false,
  selectedCancelReason: null,
  cancelOtherReason: '',
  cancelError: null,
  isCancellingSubscription: false
}

const CancelSubscription = ({ user, product, onSuccess }) => {

  const { client } = useContext(AppConfigContext)

  const [cancelSubscriptionState, setCancelSubscriptionState] = useState(initialState)

  if (!user || !product) return null

  const userId = user.user_id || user._id

  const resetState = () => {
    setCancelSubscriptionState(initialState)
  }

  const handleClose = () => {
    resetState()
    setCancelSubscriptionState(prev => ({ ...prev, showCancelSubscriptionModal: false }))
  }

  const handleCancel = () => {

    if (!cancelSubscriptionState.selectedCancelReason) {
      setCancelSubscriptionState(prev => ({ ...prev, cancelError: 'Please select a reason' }))
      return
    }

    if (cancelSubscriptionState.selectedCancelReason.value === 'Others' && !cancelSubscriptionState.cancelOtherReason.trim()) {
      setCancelSubscriptionState(prev => ({ ...prev, cancelError: 'Please enter your reason' }))
      return
    }

    const finalReason =
      cancelSubscriptionState.selectedCancelReason.value === 'Others'
        ? cancelSubscriptionState.cancelOtherReason.trim()
        : cancelSubscriptionState.selectedCancelReason.value

    const api = ApiHelper(client, 'CANCEL_SUBSCRIPTION')

    const requestBody = {
      co_user_id: userId,
      product_id: product._id,
      reason: finalReason,
      client_secret: Constants.CLIENT_SECRET
    }

    const body = _.pick(requestBody, api.API_BODY || [])

    setCancelSubscriptionState(prev => ({ ...prev, isCancellingSubscription: true }))

    api.API(client, body)
      .then(res => {

        const isSuccess =
          res?.status === Constants.CO_API.SUCCESS &&
          res?.data?.status !== 'failed'

        if (isSuccess) {

          showToastNotification({
            message: 'Subscription cancelled successfully',
            isSuccess: true
          })

          onSuccess?.(userId)
          handleClose()

        } else {

          showToastNotification({
            message: res?.message || 'Cancel failed',
            isSuccess: false
          })

        }

      })
      .catch((err) => {
        console.error(err)
        const message = err?.response?.data?.message 
        || err?.message 
        || 'Something went wrong'
        showToastNotification({ message, isSuccess: false })
      })
      .finally(() => 
        setCancelSubscriptionState(prev => ({ ...prev, isCancellingSubscription: false })))
  }

  return (
    <>
      <Button
        size="sm"
        variant="outline-primary"
        onClick={() => setCancelSubscriptionState(prev => ({ ...prev, showCancelSubscriptionModal: true }))}
      >
        Cancel
      </Button>
      <Modal
        show={cancelSubscriptionState.showCancelSubscriptionModal}
        onHide={handleClose}
        dialogClassName="cancel-subscription-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title className="cancel-subscription-title">
            Are you sure you want to cancel your credit subscription?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>

          <div className="cancel-subscription-warning-section">
            <div className="cancel-subscription-icon">
              <FiAlertTriangle size={22} />
            </div>
            <div className="cancel-subscription-content">
              <p className="cancel-subscription-message">
                You will be <b>downgraded to the Freemium plan</b> at the end of your current subscription
                 cycle if you cancel the subscription.
              </p>
              <div className="cancel-subscription-subheading">
                By Cancelling Your Subscription you will:
              </div>
              <ul className="cancel-subscription-list">
                <li>Purchased credits will be available with your account</li>
                <li>Limits will be reduced to Freemium plan after this subscription ends</li>
                <li>Please let us know the reason for cancellation</li>
              </ul>
            </div>
          </div>
          <div className="cancel-subscription-select-container">
            <Select
              options={CANCEL_REASONS}
              placeholder="Select"
              value={cancelSubscriptionState.selectedCancelReason}
              onChange={(value) => {
                setCancelSubscriptionState(prev => ({ ...prev, selectedCancelReason: value, cancelError: null }))
              }}
              isSearchable={false}
            />
          </div>
          {cancelSubscriptionState.selectedCancelReason?.value === 'Others' && (
            <Form.Group className="cancel-subscription-textarea-container">
              <Form.Control
                as="textarea"
                rows={1}
                placeholder="Enter here"
                value={cancelSubscriptionState.cancelOtherReason}
                onChange={(e) => {
                  setCancelSubscriptionState(prev => ({ ...prev, cancelOtherReason: e.target.value, cancelError: null }))
                }}
                className="cancel-subscription-textarea"
              />
            </Form.Group>
          )}
          {cancelSubscriptionState.cancelError && (
            <div className="cancel-subscription-error">
              {cancelSubscriptionState.cancelError}
            </div>
          )}

        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="link"
            onClick={handleClose}
            className="cancel-subscription-link-btn"
          >
            I don&apos;t want to cancel
          </Button>
          <Button
            variant="outline-primary"
            onClick={handleCancel}
            disabled={
              cancelSubscriptionState.isCancellingSubscription ||
              !cancelSubscriptionState.selectedCancelReason ||
              (cancelSubscriptionState.selectedCancelReason?.value === 'Others' && !cancelSubscriptionState.cancelOtherReason.trim())
            }
          >
            {cancelSubscriptionState.isCancellingSubscription ? 'Cancelling...' : 'Continue cancellation'}
          </Button>
        </Modal.Footer>

      </Modal>
    </>
  )
}

export default CancelSubscription